import type { WsMessage } from "@/api/websocket";
import {
  bindContextualDiscoveryExecution,
  isContextualDiscoveryPresentationHeld,
} from "@/desktop/onboarding/contextual-discovery-presentation";
import { getOutboundScreensForContext } from "@/desktop/outbound-screenshot-metadata";
import { normalizeAssistantText } from "@/lib/assistant-text-normalization";
import { rebindLoadedConversationFrameRuntime } from "@/lib/conversation-frame-runtime-binding";
import { bindConversationFrameMetadata } from "@/lib/conversation-frame-surface";
import { stripCursorDisplayTokens } from "@/lib/cursor-token-display";
import { directApprovalNotice } from "@/lib/direct-approval-notice";
import { directResultNotice } from "@/lib/direct-result-notice";
import { localConversationFrameForRequest } from "@/lib/local-conversation-frame-correlation";
import { nativeResultNotice } from "@/lib/native-result-notice";
import { nextPromptFromMetadata } from "@/lib/next-prompt-metadata";
import { resolveAgentPoint } from "@/lib/pointing-transform";
import { steeredIntoExecutionId } from "@/lib/steered-prompt-metadata";
import { applyNativeConversationMessage } from "./native-conversation-message";
import { messageAttachments } from "./message-attachments";
import {
  scheduleTerminalCursorRemoval,
  terminalCursorHoldMs,
} from "./terminal-cursor-lifetime";

import { stringArray } from "./companion-frame-reducer-frames";
import type { FrameApplyContext } from "./companion-frame-reducer-shared";
import {
  logTtsPipeline,
  resolveCursorKeyForExecution,
} from "./companion-frame-reducer-shared";
export function applyMessageEvent(
  msg: WsMessage,
  ctx: FrameApplyContext,
): void {
  if (applyNativeConversationMessage(msg, ctx.conversationStore)) return;
  const d = msg.data as Record<string, unknown>;
  const rawContent = d.content;
  const content =
    typeof rawContent === "string"
      ? rawContent
      : (((rawContent as Record<string, unknown> | null)?.text as string) ??
        JSON.stringify(rawContent));
  const role = d.role as "user" | "assistant" | "system";
  const metadata = d.metadata as Record<string, unknown> | undefined;
  const messageExecutionId =
    typeof d.execution_id === "string"
      ? d.execution_id
      : typeof metadata?.execution_id === "string"
        ? metadata.execution_id
        : undefined;
  const messageSessionId =
    typeof d.runtime_session_id === "string"
      ? d.runtime_session_id
      : typeof metadata?.runtime_session_id === "string"
        ? metadata.runtime_session_id
        : undefined;
  const messageConversationId =
    typeof d.conversation === "string"
      ? d.conversation
      : typeof d.conversation_id === "string"
        ? d.conversation_id
        : typeof metadata?.conversation_id === "string"
          ? metadata.conversation_id
          : undefined;
  const messageClientRequestId =
    typeof d.client_request_id === "string"
      ? d.client_request_id
      : typeof metadata?.client_request_id === "string"
        ? metadata.client_request_id
        : undefined;
  bindContextualDiscoveryExecution({
    execution_id: messageExecutionId,
    client_request_id: messageClientRequestId,
    conversation_id: messageConversationId,
    project_id: metadata?.project_id,
  });
  const conversationFrameId =
    typeof d.conversation_frame_id === "string"
      ? d.conversation_frame_id
      : typeof metadata?.conversation_frame_id === "string"
        ? metadata.conversation_frame_id
        : localConversationFrameForRequest(
            messageClientRequestId,
            ctx.projectStore.getState().currentSpace?.id,
          );
  if (conversationFrameId && ctx.ownsSideEffects !== false) {
    void bindConversationFrameMetadata({
      frameId: conversationFrameId,
      preserveSelectedConversation: true,
      clientRequestId: messageClientRequestId,
      conversationId: messageConversationId,
      runtimeSessionId: messageSessionId,
      executionId: messageExecutionId,
    });
  }
  if (role === "user") {
    rebindLoadedConversationFrameRuntime({
      conversationId: messageConversationId,
      executionId: messageExecutionId,
      messageSequence: typeof d.sequence === "number" ? d.sequence : undefined,
      sessionId: messageSessionId,
      spaceId: ctx.projectStore.getState().currentSpace?.id,
    });
  }
  const displayContent = stripCursorDisplayTokens(content);
  const conversationMessage = {
    id: d.id as string,
    backgroundResult: nativeResultNotice(metadata, displayContent),
    directResult: directResultNotice(metadata, displayContent),
    directApproval: directApprovalNotice(metadata),
    role,
    attachments: messageAttachments(rawContent),
    content:
      role === "assistant"
        ? normalizeAssistantText(displayContent)
        : displayContent,
    conversationId: messageConversationId,
    executionId: messageExecutionId,
    steeredIntoExecutionId: steeredIntoExecutionId(metadata),
    sessionId: messageSessionId,
    conversationFrameId,
    clientRequestId: messageClientRequestId,
    nextPrompt: nextPromptFromMetadata(metadata),
    createdAt:
      (d.created_at as string) ?? msg.timestamp ?? new Date().toISOString(),
  };
  if (conversationMessage.role === "assistant") {
    if (conversationMessage.executionId) {
      logTtsPipeline(
        `assistant_final_message execution_id=${conversationMessage.executionId} text_len=${conversationMessage.content.length}`,
      );
    }
    ctx.conversationStore
      .getState()
      .finalizeAssistantMessage(conversationMessage);
    if (conversationMessage.executionId) {
      const messageSpaceId =
        (typeof metadata?.project_id === "string"
          ? metadata.project_id
          : typeof metadata?.space_id === "string"
            ? metadata.space_id
            : undefined) ??
        ctx.projectStore.getState().currentProject?.id ??
        ctx.projectStore.getState().currentSpace?.id;
      const cursorKey = resolveCursorKeyForExecution(
        ctx,
        conversationMessage.executionId,
        messageSpaceId,
      );
      const cursor = cursorKey
        ? ctx.canvasStore.getState().agentCursors.get(cursorKey)
        : undefined;
      if (cursor) {
        ctx.canvasStore.getState().setAgentCursor({
          ...cursor,
          content: conversationMessage.content,
          contentFull: conversationMessage.content,
          lastSeen: conversationMessage.createdAt,
        });
        const executionStatus = ctx.companionStore
          .getState()
          .executions.get(conversationMessage.executionId)?.status;
        if (executionStatus === "completed" || executionStatus === "paused") {
          scheduleTerminalCursorRemoval(
            ctx,
            cursor.agentId,
            conversationMessage.executionId,
            terminalCursorHoldMs(conversationMessage.content),
          );
        }
      }
      const origin =
        ctx.companionStore
          .getState()
          .executions.get(conversationMessage.executionId)?.origin ===
        "heartbeat"
          ? "heartbeat"
          : "chat";
      if (
        !isContextualDiscoveryPresentationHeld(conversationMessage.executionId)
      )
        ctx.ttsStore
          .getState()
          .speakFinalReply(
            conversationMessage.executionId,
            conversationMessage.content,
            origin,
          );
    }
  } else if (conversationMessage.role === "user") {
    ctx.conversationStore.getState().reconcileUserMessage(conversationMessage);
  } else {
    ctx.conversationStore.getState().pushMessage(conversationMessage);
  }
  // Pointing pipeline: resolve [POINT:…] token to global desktop coords.
  if (d.role === "assistant" && msg.device_local !== false) {
    const pointAt = (
      metadata as
        | { point_at?: { screen: number; x: number; y: number } }
        | undefined
    )?.point_at;
    if (pointAt) {
      const resolved = resolveAgentPoint(
        pointAt,
        getOutboundScreensForContext({
          executionId: messageExecutionId,
          sessionId:
            typeof d.runtime_session_id === "string"
              ? d.runtime_session_id
              : undefined,
          imageIds: stringArray(d.image_ids),
        }),
      );
      if (resolved) {
        const receivedAt = Date.now();
        const spaceId = ctx.projectStore.getState().currentSpace?.id;
        const cursorKey =
          resolveCursorKeyForExecution(ctx, messageExecutionId, spaceId) ??
          ((d.agent_id as string | undefined) || "");
        ctx.pointingStore.getState().setLastPoint({
          agentId: cursorKey,
          displayId: resolved.displayId,
          globalX: resolved.globalX,
          globalY: resolved.globalY,
          receivedAt,
        });
        if (cursorKey) {
          ctx.canvasStore.getState().setAgentCursorPoint(cursorKey, {
            displayId: resolved.displayId,
            globalX: resolved.globalX,
            globalY: resolved.globalY,
            seq: receivedAt,
          });
        }
      }
    }
  }
}
